// รายการเครื่องเช่า: the rental machines (NetSuite fixed assets) that can be put on a
// contract, with their serial numbers. The list is a copy; anyone can refresh it here.
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../config/api';

const PAGE_SIZE = 50;

const fmtTime = (value) => {
  if (!value) return 'ยังไม่เคยอัปเดต';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('th-TH', { day: 'numeric', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const RentalAssets = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [types, setTypes] = useState([]);
  const [sync, setSync] = useState({ count: null, syncedAt: null });
  const [query, setQuery] = useState('');
  const [type, setType] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await axios.get(`${API_URL}/rental/assets`, {
        params: { q: query, type, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE, types: 1 }
      });
      setItems(resp.data.items || []);
      setTotal(resp.data.total || 0);
      setTypes(resp.data.types || []);
      setSync({ count: resp.data.count, syncedAt: resp.data.synced_at });
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดรายการเครื่องเช่าไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  }, [query, type, page]);

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const resp = await axios.post(`${API_URL}/rental/assets/refresh`);
      toast.success(`อัปเดตรายการเครื่อง ${resp.data.count.toLocaleString('th-TH')} เครื่องแล้ว`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'อัปเดตรายการเครื่องไม่สำเร็จ');
    } finally {
      setRefreshing(false);
    }
  };

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="rs wlog">
      <header className="rs-header">
        <div>
          <Link className="econ-link-btn" to="/rental-so"><i className="bi bi-arrow-left"></i> เอกสารเครื่องเช่า</Link>
          <h1 className="rs-title">รายการเครื่องเช่า</h1>
          <p className="rs-subtitle">
            เครื่องจาก NetSuite {sync.count != null ? `${sync.count.toLocaleString('th-TH')} เครื่อง` : ''} · อัปเดตล่าสุด {fmtTime(sync.syncedAt)}
          </p>
        </div>
        <button type="button" className="rs-btn rs-btn--ghost" onClick={refresh} disabled={refreshing}>
          <i className={`bi bi-arrow-clockwise${refreshing ? ' econ-spin' : ''}`}></i> {refreshing ? 'กำลังอัปเดต...' : 'อัปเดตรายการจาก NetSuite'}
        </button>
      </header>

      <div className="wlog__filters">
        <div className="rs-search" style={{ flex: '1 1 260px' }}>
          <i className="bi bi-search"></i>
          <input className="rs-input" type="text" value={query} placeholder="ค้นหา Serial, เลขทรัพย์สิน, ชื่อเครื่อง..."
            onChange={(e) => { setQuery(e.target.value); setPage(1); }} />
        </div>
        <select className="rs-input" style={{ flex: '0 1 280px' }} value={type} aria-label="กรองตามประเภท"
          onChange={(e) => { setType(e.target.value); setPage(1); }}>
          <option value="">ทุกประเภท</option>
          {types.map(t => <option key={t.type} value={t.type}>{t.type} ({t.count.toLocaleString('th-TH')})</option>)}
        </select>
      </div>

      {error && <div className="econ-alert" role="alert"><div className="econ-alert__line">{error}</div></div>}

      <div className="rs-card" style={{ padding: 0 }}>
        <div className="rs-table-wrap" style={{ border: 0 }}>
          <table className="rs-table">
            <thead>
              <tr><th>Serial No.</th><th>เลขทรัพย์สิน</th><th>ชื่อเครื่อง</th><th>ประเภท</th><th>สถานะ</th></tr>
            </thead>
            <tbody>
              {loading && items.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', color: '#65645f' }}>กำลังโหลด...</td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', color: '#65645f' }}>ไม่พบเครื่องที่ค้นหา</td></tr>}
              {items.map(a => (
                <tr key={a.asset_number}>
                  <td className="rs-mono">{a.serial_number || <span className="text-muted">ไม่มี Serial</span>}</td>
                  <td className="rs-mono">{a.asset_number}</td>
                  <td>{a.asset_name || '—'}</td>
                  <td>{a.asset_type || '—'}</td>
                  <td>
                    {a.in_use_by
                      ? <span className="wlog__pill wlog__pill--bad">ใช้อยู่ในสัญญา {a.in_use_by}</span>
                      : <span className="wlog__pill wlog__pill--ok">ว่าง</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rs-assetsync">
        <span>พบ {total.toLocaleString('th-TH')} เครื่อง · หน้า {page} / {pages}</span>
        <span>
          <button type="button" className="econ-link-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><i className="bi bi-chevron-left"></i> ก่อนหน้า</button>
          {' '}
          <button type="button" className="econ-link-btn" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>ถัดไป <i className="bi bi-chevron-right"></i></button>
        </span>
      </div>
    </div>
  );
};

export default RentalAssets;
